import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Filter, ArrowUpDown, X, Clock, Info } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

type StatusFilter = "Used" | "Unused" | "Unranked" | "Archived" | "all";
type ExpirationFilter = "fresh" | "expired" | "all";

interface FilterControlsProps {
  onFilterChange: (status: StatusFilter) => void
  onExpirationChange?: (expiration: ExpirationFilter) => void
  onSortChange: (sort: string) => void
  onClearFilters: () => void
  loading?: boolean
  currentFilter: StatusFilter
  currentExpiration?: ExpirationFilter
  currentSort: string
}

export function FilterControls({
  onFilterChange,
  onExpirationChange,
  onSortChange,
  onClearFilters,
  loading = false,
  currentFilter,
  currentExpiration = 'fresh',
  currentSort
}: FilterControlsProps) {
  return (
    <TooltipProvider>
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Status Filter */}
        <Select
          value={currentFilter}
          onValueChange={onFilterChange}
          disabled={loading}
        >
          <SelectTrigger className="w-full sm:w-[160px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Unused">Unused</SelectItem>
            <SelectItem value="Used">Used</SelectItem>
            <SelectItem value="Unranked">Unranked</SelectItem>
            <SelectItem value="Archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        {/* Expiration Filter with Tooltip */}
        <div className="flex items-center gap-2">
          <Select
            value={currentExpiration}
            onValueChange={onExpirationChange}
            disabled={loading}
          >
            <SelectTrigger className="w-full sm:w-[140px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
              <Clock className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Expiration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fresh">Fresh</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-white/30 hover:bg-white/50 transition-colors"
                disabled
              >
                <Info className="h-4 w-4 text-blue-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-sm">
                The system analyzes the lifespan of each trend or news item and automatically expires convergences once they are likely to be out of the news or hype cycle.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

      {/* Sort Dropdown */}
      <Select
        value={currentSort}
        onValueChange={onSortChange}
        disabled={loading}
      >
        <SelectTrigger className="w-full sm:w-[180px] h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors">
          <ArrowUpDown className="h-4 w-4 mr-2" />
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="most-recent">Most Recent</SelectItem>
          <SelectItem value="virality">Highest Virality</SelectItem>
          <SelectItem value="relevance">Highest Relevance</SelectItem>
          <SelectItem value="power">Highest Power</SelectItem>
        </SelectContent>
      </Select>
      
        {/* Clear Filters Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={onClearFilters}
          disabled={loading || (currentFilter === "all" && currentExpiration === "fresh" && currentSort === "most-recent")}
          className="h-12 px-4 bg-white/50 border-white/50 hover:bg-white/70 transition-colors"
        >
          <X className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
      </div>
    </TooltipProvider>
  )
}
